import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  CircularProgress,
  TextField,
} from '@mui/material';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { CatalogEntry, useFerryVehicleModels } from '../queries';
import { useAutocompleteFocus } from '../useAutocompleteFocus';
import { vehicleLabel } from '../ferry';

interface Props {
  label: string;
  value: CatalogEntry | null;
  onChange: (entry: CatalogEntry | null) => void;
  width?: number;
}

/**
 * Vehículo a embarcar (opcional: sin vehículo los pasajeros van a pie). El
 * maestro tiene ~9k modelos, así que este catálogo SÍ es remoto: `q` viaja al
 * hub con debounce. La tarifa depende de las DIMENSIONES, que se muestran.
 */
export function FerryVehicleInput({ label, value, onChange, width = 320 }: Props) {
  const { t } = useTranslation();
  const [debouncedQuery, setQuery] = useDebouncedState('', 350);
  // `vehicleLabel` es la MISMA etiqueta que usa `getOptionLabel` abajo: si
  // difirieran, MUI repondría el input contra la suya (#2226).
  const focus = useAutocompleteFocus({
    selectedLabel: value ? vehicleLabel(value) : '',
    onQueryChange: setQuery,
  });
  const { data, isFetching } = useFerryVehicleModels(debouncedQuery);
  // Aislado (#2226): solo se enciende mientras el asesor tipea EN ESTE campo.
  const showLoading = focus.editing && isFetching && debouncedQuery.trim().length >= 2;

  return (
    <Autocomplete
      size="small"
      sx={{ width }}
      options={data ?? []}
      loading={showLoading}
      value={value}
      onChange={(_event, entry, reason) => {
        if (focus.commitValueChange(reason)) onChange(entry);
      }}
      inputValue={focus.inputValue}
      onFocus={focus.onFocus}
      onBlur={focus.onBlur}
      onInputChange={focus.onInputChange}
      getOptionLabel={vehicleLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      filterOptions={(opts) => opts}
      noOptionsText={
        debouncedQuery.trim().length < 2
          ? t('travelServices.ferry.typeToSearch')
          : t('travelServices.ferry.noVehicles')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('travelServices.ferry.vehiclePlaceholder')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {showLoading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
